import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { Field } from 'formik';
import type React from 'react';
import { useState } from 'react';
import { ToggleSwitch } from '../ToogleSwitch';

type Option = {
  value: string;
  label: string;
};

export interface SelectFieldProps {
  id?: string;
  label?: string;
  extra?: React.ReactNode;
  placeholder?: string;
  name: string;
  options?: Option[];
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rightElement?: React.ReactNode;
  fontSize?: string | number;
  fontWeight?: string;
  extraInfo?: React.ReactNode;
  italic?: boolean;
  partialLeft?: boolean;
  partialRight?: boolean;
  labelFontSize?: string;
  labelMb?: string;
  sideLabel?: boolean;
  isDisabled?: boolean;
  placeholderFontColor?: string;
  placeholderFontSize?: string;
  placeholderFontWeight?: string;
  tooltipElement?: React.ReactNode;
  toggleOptions?: {
    option1: Option[];
    option2: Option[];
  };
  toggleOptionsLabels?: string[];
  withToggleOptions?: boolean;
  defaultToggleOption?: string;
  hideErrorMessage?: boolean;
  [x: string]: any;
}

export function SelectField(props: SelectFieldProps) {
  const {
    id,
    label,
    extra,
    errors,
    touched,
    placeholder,
    mb,
    isRequired,
    name,
    validate,
    full,
    rightElement,
    minW,
    maxW,
    fontSize,
    fontWeight,
    extraInfo,
    italic,
    options,
    partialLeft,
    partialRight,
    labelFontSize,
    labelMb,
    sideLabel,
    isDisabled,
    placeholderFontColor,
    placeholderFontWeight,
    placeholderFontSize,
    tooltipElement,
    withToggleOptions = false,
    toggleOptions,
    toggleOptionsLabels,
    defaultToggleOption,
    hideErrorMessage,
    onDark,
    ...rest
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const isError = !!(errors && errors.length > 0);
  const defaultOption = defaultToggleOption || '';
  const [selected, setSelected] = useState<string>(defaultOption);

  const handleToogleChange = (option: string) => {
    setSelected(option);
  };

  const currentOptions: Option[] | undefined =
    withToggleOptions && toggleOptions && toggleOptionsLabels
      ? selected === toggleOptionsLabels[0]
        ? toggleOptions.option1
        : toggleOptions.option2
      : options;

  return (
    <Flex
      direction="column"
      mb={mb ?? '30px'}
      width={full ? 'full' : 'unset'}
      minW={minW}
      maxW={maxW}
    >
      <FormControl isRequired={isRequired} isInvalid={isError && touched} isDisabled={isDisabled}>
        <Flex flexDir={sideLabel ? 'row' : 'column'} gap={sideLabel ? 3 : 0}>
          {label && (
            <HStack justifyContent="space-between" flexShrink={0}>
              <FormLabel
                flexShrink={sideLabel ? 0 : 1}
                display="flex"
                htmlFor={id}
                fontSize={labelFontSize || '2xl'}
                lineHeight="normal"
                color={textColorPrimary}
                fontWeight="bold"
                mb={labelMb || 4}
                alignSelf={sideLabel ? 'center' : 'baseline'}
                _hover={{ cursor: 'pointer' }}
              >
                {label}
              </FormLabel>
              {withToggleOptions && toggleOptionsLabels && (
                <Box mr="auto" alignSelf="flex-start">
                  <ToggleSwitch
                    leftOption={toggleOptionsLabels[0]}
                    rightOption={toggleOptionsLabels[1]}
                    defaultOption={defaultOption}
                    onChange={handleToogleChange}
                  />
                </Box>
              )}
              {tooltipElement && tooltipElement}
            </HStack>
          )}
          <Field
            as={Select}
            name={name}
            id={id}
            bg="blue.800"
            border="3px solid white"
            borderRadius="10px"
            fontWeight={fontWeight ?? '500'}
            color={textColorPrimary}
            fontSize={fontSize ?? '20px'}
            outline={isError && touched && '1px solid red'}
            placeholder={placeholder}
            h="60px"
            maxH="60px"
            style={{ fontStyle: italic ? 'italic' : 'normal', color: textColorPrimary}}
            css={{ paddingLeft: '28px' }}
            _placeholder={{
              fontWeight: placeholderFontWeight ? placeholderFontWeight : '600',
              color: placeholderFontColor ? placeholderFontColor : 'gray.600',
              fontSize: placeholderFontSize ? placeholderFontSize : '20px',
            }}
            {...rest}
            borderLeftRadius={partialRight ? '0' : '10px'}
            borderLeftWidth={partialRight ? '0' : '3px'}
            borderRightRadius={partialLeft ? '0' : '10px'}
          >
            {currentOptions?.map((option) => (
              <option key={option.value} value={option.value} style={{ color: 'black' }}>
                {option.label}
              </option>
            ))}
          </Field>
        </Flex>
        {!hideErrorMessage && isError && touched && (
          <FormErrorMessage ml="10px">{errors}</FormErrorMessage>
        )}
      </FormControl>
    </Flex>
  );
}
